// Value-resolution helpers shared by the payroll screens (EmployeePayrollDetails)
// and the payslip PDF (PayslipPdf). They live here, at module scope, rather than
// inside those components so the unit tests can import and exercise the SAME code
// that ships: the earlier tests re-declared private copies of these helpers, so a
// regression in the shipped logic would have left them green (CRM #48343).
//
// The thread running through all of them: a stored ₹0 is a real amount. `a || b`
// treats 0 as missing and silently substitutes a stale/sticky figure, which on a
// payslip means printing a number the employee was never paid.

// Number coercion used across both files. Kept here so the tests bind to one
// definition rather than a copy.
export const n = (v) => Number(v || 0);

// First value that is actually SET, scanning left to right. A real 0 (or '0') wins;
// only null / undefined / '' falls through to the next candidate. Note that
// `value > 0 ? value : fallback` is the same defect in different clothes and is not
// a substitute for this.
export const firstSet = (...vals) => vals.find((v) => v !== undefined && v !== null && v !== '');

// Does a history row (incentive / TDS / deduction) belong to the given period?
// Month is compared case-insensitively by name, year numerically.
export const isSameMonthYear = (item, month, year) => {
  if (!item) return false;
  return (
    String(item.month || '').toLowerCase() === String(month || '').toLowerCase() &&
    Number(item.year) === Number(year)
  );
};

// Sum a history list for one period.
//
// Returns null ONLY when the period has no matching row at all — that is the single
// case where the caller may fall back to the flat sticky field. A period whose rows
// add up to zero returns 0 and wins, because that zero is the recorded amount for
// the month. (The old `rows.reduce(...) || flat` fell through on a genuine zero and
// printed the sticky amount instead.)
export const historyTotalForMonth = (rows, key, month, year) => {
  const matches = (rows || []).filter((it) => isSameMonthYear(it, month, year));
  return matches.length ? matches.reduce((s, it) => s + n(it?.[key]), 0) : null;
};

// ACTUAL column resolver: the amount payroll actually prorated for the period
// (join date / LOP / partial days), falling back to the full-month value only when
// the record genuinely lacks the prorated field (older / imported payslips). The
// check is nullish, not truthy, so a prorated ₹0 is respected.
export const actualOrFull = (data, actualKey, fullKey) => {
  const v = data?.[actualKey];
  return v === undefined || v === null || v === '' ? n(data?.[fullKey]) : n(v);
};

// Stored backend totals take precedence over the frontend-computed sum so these
// screens and the server-rendered PDF never drift from the stored payslip record.
// A stored 0 is honoured. `requirePositive` is for the full_* total columns, where
// 0 means "never populated" rather than a real zero total.
export const storedTotalOr = (stored, computed, { requirePositive = false } = {}) => {
  const isSet = stored !== undefined && stored !== null && stored !== '';
  if (!isSet) return computed;
  if (requirePositive && !(n(stored) > 0)) return computed;
  return n(stored);
};
